import { useEffect, useState } from 'react';

import {
  Card,
  ClassicCard,
  openSession,
  removeSubscription,
  requestContentCardsRefresh,
  subscribeToContentCardsUpdates,
} from '@braze/web-sdk';
import { captureMessage } from '@sentry/browser';

import BrazeInitializer from '../brazeInitializer';
import {
  BrazeSdkStatus,
  EmployeeContentCard,
  RewardsContentCard,
  UseEmployeeContentCardReturn,
  UseRewardsContentCardReturn,
} from '../data';

const isValidContentCard = (card: Card): card is ClassicCard => {
  return 'title' in card && 'description' in card && 'extras' in card && card.extras !== undefined;
};

const filterEmployeeContentCards = (card: Card): card is EmployeeContentCard => {
  return isValidContentCard(card) && card.extras?.cardSystem === 'employee_tasks';
};

const filterRewardsCards = (card: Card): card is RewardsContentCard => {
  return isValidContentCard(card) && card.extras?.cardSystem === 'rewards';
};

/*
  The useBrazeContentCard hook is responsible for subscribing to the content card updates and return the content card to the consumer.
  The actual initialization of the SDK is handled by the brazeInitializer module. When a list of cards are returned from Braze,
  we take that list of raw Cards and filter them down to only the cards that are valid through the isValidContentCard function.
  We then sort the cards based on the priority list that is passed in through the configs object.

  Once we have a set of filtered cards, we grab the top card and return it to the consumer. If there are no cards, undefined will be returned.

  The general status flow of the SDK lifecycle follows the below state flow chart
  https://user-images.githubusercontent.com/5324658/217916180-42ecd4e4-46c7-4262-b6a9-932df426d450.png
*/
export const useBrazeContentCard = () => {
  const brazeInitializer = BrazeInitializer.getInstance();
  const [contentCards, setContentCards] = useState<ClassicCard[]>([]);
  const [status, setStatus] = useState<BrazeSdkStatus>(brazeInitializer.status);
  const isInitialized = brazeInitializer.status === 'initialized';

  useEffect(() => {
    let isMounted = true;
    if (status === 'uninitialized') {
      void (async () => {
        await brazeInitializer.initialize();
        if (isMounted) setStatus(brazeInitializer.status);
      })();
    }
    return () => {
      isMounted = false;
    };
  }, [status, brazeInitializer]);

  useEffect(() => {
    if (status === 'initialized') setStatus('isRefreshing');
    if (status === 'isRefreshing') {
      requestContentCardsRefresh(undefined, () => {
        setStatus('failed');
        captureMessage('Braze content card refresh failed');
      });
    }
  }, [status]);

  useEffect(() => {
    if (isInitialized) {
      const subscriptionId = subscribeToContentCardsUpdates(cards => {
        setStatus('hasContent');
        setContentCards(prevCards => {
          const recentCards = cards.cards.filter(isValidContentCard);
          if (recentCards.length === 0 && prevCards.length > 0) {
            return prevCards;
          }
          return recentCards;
        });
      });
      openSession();
      return () => {
        if (subscriptionId) {
          removeSubscription(subscriptionId);
        }
      };
    }

    return () => null;
  }, [isInitialized]);

  return {
    cards: contentCards,
    isRefreshing: status === 'isRefreshing',
    refresh: (dismissalCardId?: string) => {
      if (dismissalCardId) {
        setContentCards(prevCards => prevCards.filter(v => v.id !== dismissalCardId));
      }

      setStatus('isRefreshing');
    },
  };
};

// This is an interface hook to get employee content cards from Braze
export const useEmployeeContentCard = (): UseEmployeeContentCardReturn => {
  const { cards, refresh, isRefreshing } = useBrazeContentCard();

  const contentCards = cards.filter(filterEmployeeContentCards);

  return {
    contentCards,
    refresh,
    isRefreshing,
  };
};

// This is an interface hook to get rewards content cards from Braze
export const useRewardsContentCard = (): UseRewardsContentCardReturn => {
  const { cards, refresh } = useBrazeContentCard();

  const [contentCard] = cards.filter(filterRewardsCards);

  return {
    contentCard,
    refresh,
  };
};
